<template>
  <div class="space-y-4">
    <h2>{{ t('inclusiveKms') }}</h2>
    <div class="space-y-4">
      <div v-for="(_, i) in fields" :key="i" class="rounded-md bg-white">
        <div class="p-4 pb-0">
          <div class="flex items-end gap-2 border-b pb-4">
            <InputField
              class="flex-1"
              :name="`inclusiveKms[${i}].pricePerExtraKmLte`"
              :label="t('toPricePerExtraKm')"
              :type="'number'"
              :max-fraction-digits="2"
              :min="1"
              adornment="€"
              placeholder="∞"
            />
            <button
              class="px-2 py-3"
              :class="{
                'text-bittersweet-600': fields.length > 1,
                'text-obsidian-400': fields.length === 1,
              }"
              :disabled="fields.length === 1"
              @click.prevent="removeStep(i)"
            >
              <XIcon class="size-4" />
            </button>
          </div>
        </div>
        <MultipleValuesListField
          :name="`inclusiveKms[${i}].kmFromDays`"
          :button-text="t('addDayStep')"
          :field-settings="fieldSettings"
          show-delete-alert
        />
      </div>
      <InputHint :hint="error" is-error />
      <div class="flex justify-center">
        <CVButton variant="ghost" @click="addStep">{{ t('addInclusiveKmStep') }}</CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useFieldArray, useFieldError } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import MultipleValuesListField, {
  type FieldSettings,
} from '@/components/MultipleValuesListField.vue';
import { computed } from 'vue';
import type { InclusiveKmsDto } from '@carvia/ros-client-types';
import InputField from '@/components/InputField.vue';
import { XIcon } from 'lucide-vue-next';
import { Alert } from '@/services/alert.service';
import InputHint from '@/components/InputHint.vue';

const { t } = useI18n();

const { fields, push, remove } = useFieldArray<InclusiveKmsDto>('inclusiveKms');

const error = useFieldError('inclusiveKms');

const fieldSettings = computed<FieldSettings[]>(() => [
  {
    name: 'fromDay',
    label: t('fromDay'),
    type: 'number',
    min: 1,
  },
  {
    name: 'kmInclusivePerDay',
    label: t('kmInclusivePerDay'),
    adornment: 'km',
    type: 'number',
    min: 0,
  },
]);

const addStep = () => {
  const newStep = {
    pricePerExtraKmLte: null,
    kmFromDays: [
      {
        fromDay: 1,
        kmInclusivePerDay: 0,
      },
    ],
  };
  push(newStep);
};

const removeStep = async (i: number) => {
  const { isDenied } = await Alert.fire({
    text: t('reallyDeleteMessage'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) {
    return;
  }
  remove(i);
};
</script>

<i18n lang="json">
{
  "en": {
    "inclusiveKms": "Inclusive km",
    "addInclusiveKmStep": "+ Add inclusive km step",
    "toPricePerExtraKm": "To price per extra km",
    "addDayStep": "+ Add day step",
    "fromDay": "From day",
    "kmInclusivePerDay": "Km inclusive per day",
    "reallyDeleteMessage": "Do you really want to delete this inclusive km step?"
  },
  "de": {
    "inclusiveKms": "Inklusiv-KM",
    "addInclusiveKmStep": "+ Inklusiv-KM-Staffelung hinzufügen",
    "toPricePerExtraKm": "Bis Preis pro Extra-KM",
    "addDayStep": "+ Tag Staffelung hinzufügen",
    "fromDay": "Ab Tag",
    "kmInclusivePerDay": "Inklusiv-KM pro Tag",
    "reallyDeleteMessage": "Möchtest du diese Inklusiv-KM-Staffelung wirklich löschen?"
  },
  "es": {
    "inclusiveKms": "Km incluidos",
    "addInclusiveKmStep": "+ Añadir tramo de km incluidos",
    "toPricePerExtraKm": "Hasta precio por km extra",
    "addDayStep": "+ Añadir tramo por día",
    "fromDay": "A partir del día",
    "kmInclusivePerDay": "Km incluidos por día",
    "reallyDeleteMessage": "¿Seguro que quieres eliminar este tramo de km incluidos?"
  }
}
</i18n>
